import React, { useState, useEffect } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { CheckCircle, XCircle, Plus, Trash2, Video } from 'lucide-react';
import { apiClient as api } from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface BunnyConfig {
  connected: boolean;
  library_id?: number;
  library_name?: string;
  allowed_domains?: string[];
  created_at?: string;
}

const VideoSettings: React.FC = () => {
  const [config, setConfig] = useState<BunnyConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/video/settings/bunny');
      setConfig(res.data);
    } catch {
      setError('Failed to load video settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      setError('Enter your Bunny.net account API key.');
      return;
    }
    setConnecting(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.post('/video/settings/bunny', { account_api_key: apiKey });
      setConfig(res.data);
      setApiKey('');
      setSuccess('Bunny.net connected. A dedicated video library has been created for your products.');
    } catch (err: any) {
      setError(getApiError(err, 'Connection failed. Check your API key.'));
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Bunny.net? Your remote video library will be preserved but videos will no longer be playable from AiMe.')) return;
    setDisconnecting(true);
    try {
      await api.delete('/video/settings/bunny');
      setConfig({ connected: false });
      setSuccess('Disconnected.');
    } catch {
      setError('Failed to disconnect.');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim();
    if (!domain) return;
    setAddingDomain(true);
    setError('');
    try {
      const res = await api.post('/video/settings/bunny/domains', { domain });
      setConfig(prev => prev ? { ...prev, allowed_domains: res.data.allowed_domains } : prev);
      setNewDomain('');
    } catch (err: any) {
      setError(getApiError(err, 'Failed to add domain.'));
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      const res = await api.delete(`/video/settings/bunny/domains/${encodeURIComponent(domain)}`);
      setConfig(prev => prev ? { ...prev, allowed_domains: res.data.allowed_domains } : prev);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to remove domain.'));
    }
  };

  if (loading) return <div className="products-loading">Loading video settings…</div>;

  return (
    <div className="video-settings">
      <GlassCard variant="default" padding="lg">
        <div className="video-settings-header">
          <Video size={20} />
          <div>
            <h3>Bunny.net Video Streaming</h3>
            <p className="video-settings-desc">
              Videos are hosted and streamed via Bunny.net CDN — your infrastructure is unaffected.
              Each video product gets a secure, token-signed player that expires after 1 hour.
            </p>
          </div>
        </div>

        {error && <div className="products-error" style={{ marginBottom: 12 }}>{error}</div>}
        {success && <div className="products-success" style={{ marginBottom: 12 }}>{success}</div>}

        {config?.connected ? (
          <>
            <div className="bunny-status-row">
              <CheckCircle size={16} style={{ color: 'var(--success)' }} />
              <span>Connected</span>
              <span className="bunny-lib-name">Library: <strong>{config.library_name}</strong> (ID: {config.library_id})</span>
            </div>

            {/* Domain allowlist */}
            <div className="bunny-domains-section">
              <h4>Allowed Domains</h4>
              <p className="editor-hint" style={{ marginBottom: 8 }}>
                Videos are locked to these domains. Your AiMe host is always included automatically.
                Add your custom seller domains here so embedded players work on your own site.
              </p>
              <div className="bunny-domain-list">
                {(config.allowed_domains || []).length === 0 ? (
                  <p className="bunny-no-domains">No custom domains added yet.</p>
                ) : (
                  (config.allowed_domains || []).map(d => (
                    <div key={d} className="bunny-domain-row">
                      <span>{d}</span>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 size={13} />}
                        onClick={() => handleRemoveDomain(d)}
                        title="Remove domain"
                      />
                    </div>
                  ))
                )}
              </div>
              <div className="bunny-domain-add-row">
                <input
                  type="text"
                  placeholder="e.g. myshop.com"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddDomain(); }}
                />
                <GlassButton
                  variant="primary"
                  size="sm"
                  icon={<Plus size={14} />}
                  onClick={handleAddDomain}
                  loading={addingDomain}
                >
                  Add
                </GlassButton>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleDisconnect}
                loading={disconnecting}
              >
                Disconnect Bunny.net
              </GlassButton>
            </div>
          </>
        ) : (
          <div className="bunny-connect-form">
            <div className="bunny-status-row">
              <XCircle size={16} style={{ color: 'var(--danger)' }} />
              <span>Not connected</span>
            </div>
            <p className="editor-hint" style={{ margin: '12px 0 8px' }}>
              Enter your Bunny.net account API key to enable video products. A dedicated library will be
              created automatically. Find your key at bunny.net → Account → API Keys.
            </p>
            <div className="bunny-key-row">
              <input
                type="password"
                placeholder="Bunny.net account API key"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleConnect(); }}
              />
              <GlassButton
                variant="primary"
                onClick={handleConnect}
                loading={connecting}
              >
                Connect
              </GlassButton>
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default VideoSettings;
